import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
  usePropertyField,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faVolumeXmark } from '@fortawesome/free-solid-svg-icons/faVolumeXmark';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons/faBoltLightning';
import { faFlagUsa } from '@fortawesome/free-solid-svg-icons/faFlagUsa';
import { faLeaf } from '@fortawesome/free-solid-svg-icons/faLeaf';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';
import { faCubes } from '@fortawesome/free-solid-svg-icons/faCubes';
import { faRecycle } from '@fortawesome/free-solid-svg-icons/faRecycle';
import { faGaugeHigh } from '@fortawesome/free-solid-svg-icons/faGaugeHigh';
import { faHammer } from '@fortawesome/free-solid-svg-icons/faHammer';
import { faIndustry } from '@fortawesome/free-solid-svg-icons/faIndustry';
import { faCanadianMapleLeaf } from '@fortawesome/free-brands-svg-icons/faCanadianMapleLeaf';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { isCanadianProperty } from '../utils/propertyCountry';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Better-Built Living intro — a short lead paragraph that explains the
// Z Modular / Better-Built Living philosophy, followed by a four-card grid of
// the headline benefits residents actually feel (quieter living, greater
// efficiency, American-made excellence, sustainable by design).
//
// Each card has an icon (chosen from a small bank) + a title + a short body.
// The icon field is a `text` slot so operators can swap it to another bank
// name without touching code — same approach as LifestyleResidentTypes.
// ============================================================================

function resolveIcon(name: string | null | undefined): IconDefinition {
  switch (name) {
    case 'faVolumeXmark': return faVolumeXmark;
    case 'faBoltLightning': return faBoltLightning;
    case 'faFlagUsa': return faFlagUsa;
    case 'faLeaf': return faLeaf;
    case 'faShieldHalved': return faShieldHalved;
    case 'faCubes': return faCubes;
    case 'faRecycle': return faRecycle;
    case 'faGaugeHigh': return faGaugeHigh;
    case 'faHammer': return faHammer;
    case 'faIndustry': return faIndustry;
    case 'faCanadianMapleLeaf': return faCanadianMapleLeaf;
    default: return faShieldHalved; // safe fallback
  }
}

const ICON_BANK_DESCRIPTION =
  'Icon name from the FontAwesome bank. Options: faVolumeXmark, faBoltLightning, faFlagUsa, faLeaf, faShieldHalved, faCubes, faRecycle, faGaugeHigh, faHammer, faIndustry, faCanadianMapleLeaf.';

// Benefit 3 ships US copy by default. On Canadian properties, any field still
// at its US default is swapped for the Canadian equivalent; operator edits win.
const BENEFIT_3_CANADIAN_DEFAULTS: Record<string, string> = {
  icon: 'faCanadianMapleLeaf',
  title: 'Canadian-Made Excellence',
};

// ============================================================================
// Slot group definitions (inline literals — required for schema extraction)
// ============================================================================

const benefit1Def: SlotGroupDefinition = {
  label: 'Benefit 1 — Quieter Living',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faVolumeXmark',
      description: ICON_BANK_DESCRIPTION,
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Quieter Living',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Experience enhanced sound insulation, thanks to steel framing and double-insulated walls that minimize noise and maximize comfort.</p>',
    },
  },
};

const benefit2Def: SlotGroupDefinition = {
  label: 'Benefit 2 — Greater Efficiency',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faBoltLightning',
      description: ICON_BANK_DESCRIPTION,
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Greater Efficiency',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Our off-site process delivers energy-efficient, durable homes with exceptional build quality that result in utility savings.</p>',
    },
  },
};

const benefit3Def: SlotGroupDefinition = {
  label: 'Benefit 3 — American-Made Excellence',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faFlagUsa',
      description: ICON_BANK_DESCRIPTION,
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'American-Made Excellence',
      description: 'Shows "Canadian-Made Excellence" on Canadian properties while left at the default.',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>We source high-quality, domestic materials for unmatched durability and craftsmanship in every home.</p>',
    },
  },
};

const benefit4Def: SlotGroupDefinition = {
  label: 'Benefit 4 — Sustainable by Design',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      defaultValue: 'faLeaf',
      description: ICON_BANK_DESCRIPTION,
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Sustainable by Design',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Our off-site process minimizes waste, ensuring eco-friendly construction with uncompromised quality and style.</p>',
    },
  },
};

// ============================================================================
// Benefit card renderer
// ============================================================================

interface BenefitCardProps {
  path: string;
  definition: SlotGroupDefinition;
  localizedDefaults?: Record<string, string>;
}

function BenefitCard({ path, definition, localizedDefaults }: BenefitCardProps) {
  const card = useSlotGroup(path, definition);
  const ref = useScrollReveal<HTMLDivElement>();

  const pick = (field: 'icon' | 'title'): string => {
    const value = card[field] as string;
    const localized = localizedDefaults?.[field];
    if (localized && value === definition.fields[field]?.defaultValue) return localized;
    return value;
  };
  const icon = pick('icon');
  const title = pick('title');

  return (
    <EditableSlotGroup groupPath={path} definition={definition} className="bbi-card-cell">
      <article ref={ref} className="reveal-fade-up bbi-card">
        <div className="bbi-card-icon-wrap" aria-hidden="true">
          <FontAwesomeIcon icon={resolveIcon(icon)} className="bbi-card-icon" />
        </div>
        {title ? <h3 className="bbi-card-title">{title}</h3> : null}
        {card.body ? (
          <div
            className="bbi-card-body"
            data-vibesite-richtext=""
            dangerouslySetInnerHTML={{ __html: card.body }}
          />
        ) : null}
      </article>
    </EditableSlotGroup>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function BetterBuiltIntro() {
  useRevealStyles();
  const introRef = useScrollReveal<HTMLDivElement>();
  const ctaColorVars = useCtaColorVars();
  const country = usePropertyField('property_address_country');
  const state = usePropertyField('property_address_state');
  const isCanadian = isCanadianProperty(country, state);

  return (
    <section className="bbi-section" style={ctaColorVars}>
      <div className="bbi-container">
        {/* Intro — centered heading + lead paragraph. */}
        <div ref={introRef} className="reveal-fade-up bbi-intro">
          <RichTextContent
            path="betterbuiltintro.heading"
            label="Heading"
            defaultValue='<p>What &ldquo;Better-Built Living&rdquo; Means For You</p>'
            as="h2"
            className="bbi-intro-heading"
          />
          <RichTextContent
            path="betterbuiltintro.body"
            label="Lead Paragraph"
            defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, &ldquo;Better-Built Living&rdquo; is more than just a slogan; it&rsquo;s our commitment to deliver homes that prioritize your comfort, quality, and peace of mind. Here&rsquo;s how our unique approach to off-site construction delivers a superior living experience.</p>'
            as="div"
            className="bbi-intro-body"
          />
        </div>

        {/* Sub-heading above the four benefit cards. */}
        <RichTextContent
          path="betterbuiltintro.subheading"
          label="Benefits Sub-heading"
          defaultValue='<p>What Life at <span data-type="field-token" data-field="property_name"></span> is Like for You</p>'
          as="h3"
          className="bbi-subheading"
        />

        {/* Four-up benefit cards (2-up on tablet, stacked on mobile). */}
        <div className="bbi-grid">
          <BenefitCard path="betterbuiltintro.benefit_1" definition={benefit1Def} />
          <BenefitCard path="betterbuiltintro.benefit_2" definition={benefit2Def} />
          <BenefitCard
            path="betterbuiltintro.benefit_3"
            definition={benefit3Def}
            localizedDefaults={isCanadian ? BENEFIT_3_CANADIAN_DEFAULTS : undefined}
          />
          <BenefitCard path="betterbuiltintro.benefit_4" definition={benefit4Def} />
        </div>

        {/* Schema-lint anchors — hidden, never interactive. The visible
            cards above pass dynamic paths to <EditableSlotGroup>, so the
            source linter can't see each literal path. These anchors carry
            the canonical paths so `slot-group-missing-editable-wrapper`
            matches. */}
        <EditableSlotGroup
          groupPath="betterbuiltintro.benefit_1"
          definition={benefit1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="betterbuiltintro.benefit_2"
          definition={benefit2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="betterbuiltintro.benefit_3"
          definition={benefit3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="betterbuiltintro.benefit_4"
          definition={benefit4Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .bbi-section {
          position: relative;
          background: var(--vibe-color-background, #ffffff);
          /* HeroSolidBG owns the chevron above; the next section (dark
             BetterBuiltConstruction) owns its own band — keep this tight so
             the intro doesn't look floated. */
          padding: 2.5rem 1.25rem 3rem;
        }
        @media (min-width: 768px) {
          .bbi-section { padding: 3.5rem 1.5rem 4rem; }
        }
        @media (min-width: 1024px) {
          .bbi-section { padding: 4.5rem 2rem 5rem; }
        }

        .bbi-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* ── Intro ── */
        .bbi-intro {
          max-width: 820px;
          margin: 0 auto 2.75rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .bbi-intro { margin-bottom: 3.5rem; }
        }

        .bbi-intro-heading,
        .bbi-intro-heading * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .bbi-intro-heading {
          margin: 0 0 1.1rem;
          font-size: clamp(1.75rem, 4vw, 2.75rem);
          line-height: 1.1;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary, #3E441D);
        }

        .bbi-intro-body,
        .bbi-intro-body * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .bbi-intro-body {
          color: rgba(17, 24, 39, 0.82);
          font-size: 1rem;
          line-height: 1.7;
          max-width: 42rem;
          margin: 0 auto;
        }
        .bbi-intro-body p { margin: 0 0 0.6rem; }
        .bbi-intro-body p:last-child { margin-bottom: 0; }

        /* ── Sub-heading above the four benefit cards ── */
        .bbi-subheading,
        .bbi-subheading * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .bbi-subheading {
          margin: 0 auto 1.75rem;
          text-align: center;
          font-size: clamp(1.4rem, 2.6vw, 1.9rem);
          line-height: 1.15;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary, #3E441D);
          max-width: 48rem;
        }
        @media (min-width: 768px) {
          .bbi-subheading { margin-bottom: 2.25rem; }
        }

        /* ── Grid ── */
        /* 4-up on desktop, 2-up on tablet, stacked on phones. */
        .bbi-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .bbi-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1.75rem;
          }
        }
        @media (min-width: 1024px) {
          .bbi-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1.75rem;
          }
        }

        /* ── Card ── */
        /* The editable wrapper is the grid item; the card must fill it so
           every card in a row matches the tallest one. */
        .bbi-card-cell {
          display: flex;
          flex-direction: column;
        }
        .bbi-card-cell > * {
          flex: 1 1 auto;
        }
        .bbi-card {
          position: relative;
          box-sizing: border-box;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          padding: 2rem 1.5rem 2rem;
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.06);
          text-align: center;
          align-items: center;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .bbi-card::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 3px;
          background: var(--cta-bg, var(--vibe-color-secondary, #3E441D));
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .bbi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px rgba(0, 0, 0, 0.1);
        }
        .bbi-card:hover::after {
          transform: scaleX(1);
        }

        .bbi-card-icon-wrap {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 3.75rem;
          height: 3.75rem;
          background: color-mix(in srgb, var(--cta-bg, var(--vibe-color-primary)) 10%, #ffffff);
          color: var(--cta-bg, var(--vibe-color-primary));
          margin-bottom: 0.25rem;
        }
        .bbi-card-icon {
          font-size: 1.6rem;
          color: var(--cta-bg, var(--vibe-color-primary));
        }

        .bbi-card-title {
          margin: 0;
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
          font-size: 1.125rem;
          line-height: 1.2;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--vibe-color-secondary, #3E441D);
        }

        .bbi-card-body,
        .bbi-card-body * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .bbi-card-body {
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.6;
          color: rgba(17, 24, 39, 0.78);
        }
        .bbi-card-body p { margin: 0 0 0.5rem; }
        .bbi-card-body p:last-child { margin-bottom: 0; }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltIntro',
  displayName: 'Better-Built Living — Intro & Benefits',
  tokens: {
    'betterbuiltintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>What &ldquo;Better-Built Living&rdquo; Means For You</p>',
    },
    'betterbuiltintro.body': {
      label: 'Lead Paragraph',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, &ldquo;Better-Built Living&rdquo; is more than just a slogan; it&rsquo;s our commitment to deliver homes that prioritize your comfort, quality, and peace of mind. Here&rsquo;s how our unique approach to off-site construction delivers a superior living experience.</p>',
    },
    'betterbuiltintro.subheading': {
      label: 'Benefits Sub-heading',
      type: 'richtext',
      defaultValue:
        '<p>What Life at <span data-type="field-token" data-field="property_name"></span> is Like for You</p>',
    },
  },
  slotGroups: {
    'betterbuiltintro.benefit_1': benefit1Def,
    'betterbuiltintro.benefit_2': benefit2Def,
    'betterbuiltintro.benefit_3': benefit3Def,
    'betterbuiltintro.benefit_4': benefit4Def,
  },
};

(BetterBuiltIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltIntro;
